import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { Alert, App, Button, Card, Col, Form, Input, InputNumber, Modal, Row, Segmented, Space, Table, Tabs, Tag, Typography } from 'antd'
import { CheckCircleOutlined, CreditCardOutlined, LogoutOutlined, ReloadOutlined } from '@ant-design/icons'
import type { ColumnsType } from 'antd/es/table'
import { useLocation, useNavigate } from 'react-router-dom'
import { consumeSellerNotice, type SellerUiNotice, sellerSettingsAPI } from '../utils/sellerApi'
import { useSellerAuth } from '../contexts/SellerAuthContext'

interface Plan {
  code: string
  name: string
  description: string
  currency: string
  currency_symbol: string
  monthly_price: number
  yearly_price: number
  product_limit: number | null
  staff_limit: number | null
  features: string[]
  comparison?: Record<string, string>
}

interface PlanComparisonRow {
  key: string
  label: string
  category?: string
}

interface SellerAddOn {
  code: string
  name: string
  group?: string | null
  currency: string
  currency_symbol: string
  monthly_price: number
  product_limit_increment?: number
  staff_limit_increment?: number
  features?: string[]
  feature_keys?: string[]
}

interface SellerAddOnGroup {
  group?: string | null
  name: string
  description?: string
  items: SellerAddOn[]
}

interface ActiveAddOn {
  id: number
  add_on_code: string
  add_on_name: string
  amount: number
  status: string
  product_limit_increment?: number
  staff_limit_increment?: number
  starts_at?: string | null
  ends_at?: string | null
}

interface EffectiveEntitlements {
  product_limit?: number | null
  staff_limit?: number | null
  base_product_limit?: number | null
  base_staff_limit?: number | null
  product_limit_increment?: number
  staff_limit_increment?: number
}

interface ProductPolicy {
  variant_policy?: string
  example?: string
}

interface CurrentPlan {
  id: number
  plan_code: string
  plan_name: string
  billing_cycle: 'monthly' | 'yearly'
  amount: number
  currency: string
  status: string
  starts_at: string
  ends_at: string
  grace_ends_at: string
  feature_snapshot?: {
    feature_labels?: string[]
  }
}

interface BillingItem {
  id: number
  amount: number
  due_date: string
  description?: string | null
  status: string
  invoice_number?: string | null
  period_start?: string | null
  period_end?: string | null
  plan_code?: string | null
  billing_cycle?: string | null
  currency?: string | null
  paid_at?: string | null
}

interface PaymentMethod {
  id: number
  type: string
  last4: string
  expiry: string
  is_default: boolean
}

interface SandboxPaymentConfig {
  enabled: boolean
  gateway: string
  mode: string
  key_id?: string | null
  currency?: string
  accepted_methods?: string[]
  requires_server_order?: boolean
  success_fields?: string[]
  note?: string
}

interface BillingResponse {
  billings: BillingItem[]
  overdue_count: number
  next_due_date?: string | null
  current_plan?: CurrentPlan | null
  current_plan_features?: string[]
  current_plan_feature_keys?: string[]
  access_state: {
    status: string
    requires_plan_activation: boolean
    in_grace_period: boolean
    buffer_hours_remaining: number
    buffer_expires_at?: string | null
    has_operational_access: boolean
  }
  plans: Plan[]
  add_ons?: SellerAddOnGroup[]
  active_add_ons?: ActiveAddOn[]
  effective_entitlements?: EffectiveEntitlements
  comparison_rows?: PlanComparisonRow[]
  product_policy?: ProductPolicy
  currency: {
    code: string
    symbol: string
  }
  sandbox_payment?: SandboxPaymentConfig
}

interface SellerFeatureRoadmapItem {
  code: string
  name: string
  status: string
  description?: string
  source_add_on?: string | null
  group?: string | null
}

interface SellerFeatureRoadmapResponse {
  message?: string
  current_plan?: CurrentPlan | null
  access_state: BillingResponse['access_state']
  effective_entitlements?: EffectiveEntitlements
  feature_roadmap?: {
    ai_features?: SellerFeatureRoadmapItem[]
  }
}

interface SellerAddOnActivationGateway {
  name: string
  key_id?: string | null
  order_id?: string | null
  subscription_id?: string | null
  amount?: number
  currency?: string
}

interface SellerAddOnActivationResponseData {
  requires_payment_confirmation?: boolean
  payment_session_token?: string
  gateway?: SellerAddOnActivationGateway
  add_on_code?: string
  subscription_id?: number
  amount?: number
  currency?: string
}

type RazorpaySuccessPayload = {
  razorpay_order_id?: string
  razorpay_subscription_id?: string
  razorpay_payment_id?: string
  razorpay_signature?: string
}

type RazorpayOptions = {
  key: string
  amount?: number
  currency?: string
  order_id?: string
  subscription_id?: string
  name?: string
  description?: string
  prefill?: {
    email?: string
    contact?: string
  }
  handler: (response: RazorpaySuccessPayload) => void
  modal?: {
    ondismiss?: () => void
  }
}

declare global {
  interface Window {
    Razorpay?: new (options: RazorpayOptions) => { open: () => void }
  }
}

const formatCurrency = (value: number, symbol: string) =>
  `${symbol}${Number(value ?? 0).toLocaleString(undefined, { maximumFractionDigits: 2 })}`

const loadRazorpayScript = (): Promise<void> =>
  new Promise((resolve, reject) => {
    if (typeof window === 'undefined') {
      reject(new Error('Razorpay checkout is not available in this environment.'))
      return
    }

    if (window.Razorpay) {
      resolve()
      return
    }

    const existing = document.querySelector<HTMLScriptElement>('script[data-razorpay-checkout="true"]')
    if (existing) {
      existing.addEventListener('load', () => resolve())
      existing.addEventListener('error', () => reject(new Error('Failed to load Razorpay checkout.')))
      return
    }

    const script = document.createElement('script')
    script.src = 'https://checkout.razorpay.com/v1/checkout.js'
    script.async = true
    script.setAttribute('data-razorpay-checkout', 'true')
    script.onload = () => resolve()
    script.onerror = () => reject(new Error('Failed to load Razorpay checkout.'))
    document.body.appendChild(script)
  })

const SettingsPage = () => {
  const [billingData, setBillingData] = useState<BillingResponse | null>(null)
  const [paymentMethods, setPaymentMethods] = useState<PaymentMethod[]>([])
  const [featureRoadmap, setFeatureRoadmap] = useState<SellerFeatureRoadmapItem[]>([])
  const [loading, setLoading] = useState(false)
  const [saving, setSaving] = useState(false)
  const [paymentModalVisible, setPaymentModalVisible] = useState(false)
  const [paymentForm] = Form.useForm<{
    type: 'card' | 'upi' | 'wallet' | 'netbanking'
    label?: string
    cardholder_name?: string
    card_number?: string
    expiry_month?: number
    expiry_year?: number
    upi_id?: string
    wallet_name?: string
    bank_name?: string
  }>()
  const [selectedBillingCycle, setSelectedBillingCycle] = useState<'monthly' | 'yearly'>('monthly')
  const [notice, setNotice] = useState<SellerUiNotice | null>(null)
  const location = useLocation()
  const navigate = useNavigate()
  const { logout, refreshSeller, hasPermission } = useSellerAuth()
  const activationMode = location.pathname.includes('/seller/plan-activation')
  const { message } = App.useApp()

  const canUpdateSettings = hasPermission('settings.update')

  const fetchBilling = useCallback(async (nextNotice?: SellerUiNotice | null) => {
    setLoading(true)
    try {
      const response = await sellerSettingsAPI.billing()
      const nextData = response.data as BillingResponse
      setBillingData(nextData)

      if (activationMode && nextData.access_state?.has_operational_access && nextNotice?.context !== 'upgrade') {
        navigate('/seller/settings', { replace: true })
      }

      return nextData
    } catch (error: any) {
      message.error(error.response?.data?.message || 'Failed to load billing')
      return null
    } finally {
      setLoading(false)
    }
  }, [activationMode, navigate, message])

  const fetchPaymentMethods = useCallback(async () => {
    try {
      const response = await sellerSettingsAPI.paymentMethods()
      setPaymentMethods(response.data.methods ?? [])
    } catch (error: any) {
      message.error(error.response?.data?.message || 'Failed to load payment methods')
    }
  }, [message])

  const fetchFeatureRoadmap = useCallback(async () => {
    try {
      const response = await sellerSettingsAPI.features()
      const nextData = response.data as SellerFeatureRoadmapResponse
      setFeatureRoadmap(nextData.feature_roadmap?.ai_features ?? [])
    } catch (error: any) {
      message.error(error.response?.data?.message || 'Failed to load feature roadmap')
    }
  }, [message])

  const openRazorpayCheckout = useCallback(async (
    gateway: {
      key_id?: string | null
      order_id?: string | null
      subscription_id?: string | null
      amount?: number
      currency?: string
    } | undefined,
    description: string,
    onConfirm: (payload: RazorpaySuccessPayload) => Promise<void>,
    checkoutMethods?: { card?: boolean; netbanking?: boolean },
  ) => {

    const gatewayOrderId = gateway?.order_id ?? null
    const gatewaySubscriptionId = gateway?.subscription_id ?? null

    if (!gateway?.key_id || (!gatewayOrderId && !gatewaySubscriptionId)) {
      message.error('Razorpay checkout is not configured for this plan payment.')
      return
    }

    try {
      await loadRazorpayScript()

      if (!window.Razorpay) {
        throw new Error('Razorpay checkout failed to initialize.')
      }

      await new Promise<void>((resolve) => {
        const options: RazorpayOptions = {
          key: gateway.key_id,
          name: 'Trendify',
          description,
          handler: async (response) => {
            try {
              await onConfirm({
                razorpay_order_id: response.razorpay_order_id || gatewayOrderId || undefined,
                razorpay_subscription_id: response.razorpay_subscription_id || gatewaySubscriptionId || undefined,
                razorpay_payment_id: response.razorpay_payment_id || '',
                razorpay_signature: response.razorpay_signature || '',
              })
            } catch (error: any) {
              message.error(error.response?.data?.message || 'Failed to confirm Razorpay payment')
            } finally {
              resolve()
            }
          },
          modal: {
            ondismiss: () => {
              message.warning('Razorpay checkout was closed before payment confirmation.')
              resolve()
            },
          },
        }

        if (checkoutMethods) {
          options.method = {
            card: Boolean(checkoutMethods.card),
            netbanking: Boolean(checkoutMethods.netbanking),
            upi: false,
            wallet: false,
          } as any
        }

        if (gatewayOrderId) {
          options.order_id = gatewayOrderId

          options.amount = gateway.amount ?? undefined
          options.currency = gateway.currency ?? undefined
        }

        if (gatewaySubscriptionId) {
          options.subscription_id = gatewaySubscriptionId
        }

        const razorpay = new window.Razorpay(options)

        razorpay.open()
      })
    } catch (error: any) {
      message.error(error.response?.data?.message || error.message || 'Failed to open Razorpay checkout')
    }
  }, [message])

  useEffect(() => {
    const nextNotice = consumeSellerNotice()
    setNotice(nextNotice)
    void Promise.all([fetchBilling(nextNotice), fetchPaymentMethods(), fetchFeatureRoadmap()])
  }, [fetchBilling, fetchPaymentMethods, fetchFeatureRoadmap])

  const addPaymentMethod = async (values: {
    type?: 'card' | 'upi' | 'wallet' | 'netbanking'
    label?: string
    cardholder_name?: string
    card_number?: string
    expiry_month?: number
    expiry_year?: number
    upi_id?: string
    wallet_name?: string
    bank_name?: string
  }) => {
    if (!canUpdateSettings) {
      message.warning('You are not permitted to update billing settings.')
      return
    }

    setSaving(true)
    try {
      await sellerSettingsAPI.addPaymentMethod(values)
      message.success('Razorpay payment preference added')
      setPaymentModalVisible(false)
      paymentForm.resetFields()
      await fetchPaymentMethods()
    } catch (error: any) {
      message.error(error.response?.data?.message || 'Failed to add payment method')
    } finally {
      setSaving(false)
    }
  }

  const renewPlan = async (planCode?: string) => {
    if (!canUpdateSettings) {
      message.warning('You are not permitted to update billing settings.')
      return
    }

    setSaving(true)
    try {
      const payload = planCode
        ? { plan_code: planCode, billing_cycle: selectedBillingCycle }
        : { billing_cycle: selectedBillingCycle }
      const response = await sellerSettingsAPI.renewPlan(payload)

      if (response.status === 202 && response.data?.data?.requires_payment_confirmation) {
        await openRazorpayCheckout(
          response.data?.data?.gateway,
          response.data?.data?.gateway?.subscription_id ? 'Seller plan subscription' : 'Seller plan payment',
          async (paymentDetails) => {
            const confirmResponse = await sellerSettingsAPI.renewPlan({
              plan_code: planCode,
              billing_cycle: selectedBillingCycle,
              payment_details: paymentDetails,
            })

            message.success(confirmResponse.data?.message || 'Seller plan updated successfully')
            await Promise.all([fetchBilling(), fetchPaymentMethods(), refreshSeller()])

            if (confirmResponse.data?.access_state?.has_operational_access && activationMode) {
              navigate('/seller/settings', { replace: true })
            }
          },
        )
        return
      }

      message.success(response.data?.message || 'Seller plan updated successfully')
      await Promise.all([fetchBilling(), fetchPaymentMethods(), refreshSeller()])

      if (response.data?.access_state?.has_operational_access && activationMode) {
        navigate('/seller/settings', { replace: true })
      }
    } catch (error: any) {
      message.error(error.response?.data?.message || 'Failed to update seller plan')
    } finally {
      setSaving(false)
    }
  }

  const purchaseAddOn = async (addOnCode: string) => {
  if (!canUpdateSettings) {
    message.warning('You are not permitted to update billing settings.')
    return
  }

  if (!billingData?.current_plan || billingData.current_plan.status !== 'active') {
    message.error('An active seller plan is required before purchasing add-ons.')
    return
  }

  setSaving(true)
  try {
    const step1 = await sellerSettingsAPI.activateAddOn({ add_on_code: addOnCode })
    const step1Data = step1.data?.data

    if (!step1Data?.requires_payment || !step1Data?.razorpay_order_id) {
      message.error(step1.data?.message || 'Unable to start Razorpay checkout for this add-on.')
      return
    }

    const gatewayKeyId = step1Data.gateway?.key_id ?? billingData?.sandbox_payment?.key_id ?? null

    await openRazorpayCheckout(
      {
        key_id: gatewayKeyId,
        order_id: step1Data.razorpay_order_id,
        amount: step1Data.amount ? Math.round(step1Data.amount * 100) : step1Data.gateway?.amount,
        currency: step1Data.gateway?.currency ?? billingData?.currency?.code ?? 'INR',
      },
      'Seller add-on payment',
      async (paymentPayload) => {
        const step2 = await sellerSettingsAPI.activateAddOn({
          add_on_code: addOnCode,
          payment_method_type: 'card',
          payment_details: {
            razorpay_order_id: paymentPayload.razorpay_order_id,
            razorpay_payment_id: paymentPayload.razorpay_payment_id ?? '',
            razorpay_signature: paymentPayload.razorpay_signature ?? '',
          },
        })
        message.success(step2.data?.message || 'Add-on purchased successfully.')
        await Promise.all([fetchBilling(), refreshSeller()])
      },
      { card: true, netbanking: true },
    )
  } catch (error: any) {
    message.error(error.response?.data?.message || 'Failed to purchase add-on')
  } finally {
    setSaving(false)
  }
}

  const setDefaultPayment = async (id: number) => {
    if (!canUpdateSettings) {
      message.warning('You are not permitted to update billing settings.')
      return
    }

    try {
      await sellerSettingsAPI.setDefaultMethod(id)
      message.success('Default payment preference updated')
      await fetchPaymentMethods()
    } catch (error: any) {
      message.error(error.response?.data?.message || 'Failed to update payment method')
    }
  }

  const deleteMethod = async (id: number) => {
    if (!canUpdateSettings) {
      message.warning('You are not permitted to update billing settings.')
      return
    }

    try {
      await sellerSettingsAPI.deleteMethod(id)
      message.success('Payment preference removed')
      await fetchPaymentMethods()
    } catch (error: any) {
      message.error(error.response?.data?.message || 'Failed to remove payment method')
    }
  }

  const currencySymbol = billingData?.currency?.symbol ?? 'Rs. '
  const currentPlan = billingData?.current_plan ?? null
  const currentPlanFeatures = billingData?.current_plan_features ?? currentPlan?.feature_snapshot?.feature_labels ?? []
  const currentPlanFeatureKeys = billingData?.current_plan_feature_keys ?? []
  const plans = billingData?.plans ?? []
  const comparisonRows = billingData?.comparison_rows ?? []
  const addOnGroups = billingData?.add_ons ?? []
  const activeAddOns = billingData?.active_add_ons ?? []
  const aiFeatureCount = featureRoadmap.length
  const activeAddOnCodes = new Set(activeAddOns.map((addOn) => addOn.add_on_code))
  const effectiveEntitlements = billingData?.effective_entitlements
  const productPolicy = billingData?.product_policy
  const sandboxPayment = billingData?.sandbox_payment
  const accessState = billingData?.access_state
  const sortedBillings = useMemo(() => billingData?.billings ?? [], [billingData])
  const upgradeNotice = notice?.context === 'upgrade' ? notice : null
  const paymentType = Form.useWatch('type', paymentForm) || 'card'
  const popularPlanCode = 'business'
  const displayPlans = useMemo(() => {
    const order = ['starter', 'business', 'enterprise']

    return [...plans].sort((left, right) => {
      const leftIndex = order.indexOf(left.code)
      const rightIndex = order.indexOf(right.code)

      if (leftIndex === -1 && rightIndex === -1) {
        return 0
      }

      if (leftIndex === -1) {
        return 1
      }

      if (rightIndex === -1) {
        return -1
      }

      return leftIndex - rightIndex
    })
  }, [plans])
  const comparisonGroups = useMemo(() => {
    return comparisonRows.reduce<Record<string, PlanComparisonRow[]>>((acc, row) => {
      const category = row.category || 'Plan features'

      if (!acc[category]) {
        acc[category] = []
      }

      acc[category].push(row)

      return acc
    }, {})
  }, [comparisonRows])

  const paymentTypeLabel = (value: string | null | undefined) => {
    switch (value) {
      case 'upi':
        return 'UPI'
      case 'wallet':
        return 'Wallet'
      case 'netbanking':
        return 'Netbanking'
      default:
        return 'Card'
    }
  }

  const billingColumns: ColumnsType<BillingItem> = [
    {
      title: 'Invoice',
      key: 'invoice',
      render: (_, record) => (
        <div>
          <div style={{ fontWeight: 600 }}>{record.invoice_number || `Invoice #${record.id}`}</div>
          <div style={{ color: '#64748b', fontSize: 12 }}>{record.description || 'Seller plan billing'}</div>
        </div>
      ),
    },
    {
      title: 'Plan',
      key: 'plan',
      render: (_, record) => (
        <div>
          <div>{record.plan_code ? record.plan_code.toUpperCase() : 'Plan'}</div>
          <div style={{ color: '#64748b', fontSize: 12 }}>{record.billing_cycle || 'cycle'}</div>
        </div>
      ),
    },
    {
      title: 'Period',
      key: 'period',
      render: (_, record) => (record.period_start && record.period_end ? `${record.period_start} to ${record.period_end}` : 'N/A'),
    },
    {
      title: 'Amount',
      key: 'amount',
      render: (_, record) => formatCurrency(Number(record.amount ?? 0), currencySymbol),
    },
    {
      title: 'Due Date',
      dataIndex: 'due_date',
      key: 'due_date',
    },
    {
      title: 'Status',
      key: 'status',
      render: (_, record) => <Tag color={record.status === 'paid' ? 'green' : record.status === 'overdue' ? 'red' : 'orange'}>{record.status}</Tag>,
    },
  ]

  const paymentColumns: ColumnsType<PaymentMethod> = [
    { title: 'Type', dataIndex: 'type', key: 'type', render: (value) => paymentTypeLabel(value) },
    {
      title: 'Details',
      key: 'details',
      render: (_, record) => (record.type === 'card'
        ? `**** **** **** ${record.last4}`
        : record.last4 || 'N/A'),
    },
    { title: 'Expiry', dataIndex: 'expiry', key: 'expiry', render: (value) => value || 'N/A' },
    { title: 'Default', key: 'default', render: (_, record) => <Tag color={record.is_default ? 'green' : 'default'}>{record.is_default ? 'Default' : 'Saved'}</Tag> },
    {
      title: 'Actions',
      key: 'actions',
      render: (_, record) => (
        <Space>
          {canUpdateSettings ? (
            <>
              {!record.is_default ? <Button onClick={() => void setDefaultPayment(record.id)}>Set Default</Button> : null}
              <Button danger onClick={() => void deleteMethod(record.id)}>
                Remove
              </Button>
            </>
          ) : (
            <Tag color="default">Read only</Tag>
          )}
        </Space>
      ),
    },
  ]

  return (
    <div style={{ padding: activationMode ? 24 : 0, maxWidth: activationMode ? 1240 : undefined, margin: activationMode ? '0 auto' : undefined }}>
      <div style={{ marginBottom: 24, display: 'flex', justifyContent: 'space-between', gap: 16, alignItems: 'flex-start', flexWrap: 'wrap' }}>
        <div>
          <h1 style={{ margin: 0, fontSize: 28 }}>{upgradeNotice ? 'Upgrade seller plan' : activationMode ? 'Activate seller plan' : 'Billing & Plans'}</h1>
          <div style={{ color: '#64748b', marginTop: 6 }}>
            {upgradeNotice
              ? 'This seller feature is not included in your current plan. Review what you already have, or upgrade to unlock more access.'
              : activationMode
                ? 'Your account is approved. Choose a plan and complete Razorpay checkout to unlock seller dashboard access.'
                : 'Your billing plan controls seller features, product limits, staff access, and renewals. Razorpay Checkout powers plan payments.'}
          </div>
        </div>
        {activationMode ? (
          <Button icon={<LogoutOutlined />} onClick={() => void logout()}>
            Sign out
          </Button>
        ) : null}
      </div>

      {upgradeNotice && currentPlan ? (
        <Card style={{ marginBottom: 24, borderColor: '#1677ff' }}>
          <Space orientation="vertical" size="middle" style={{ width: '100%' }}>
            <Alert
              type="info"
              showIcon
              title={upgradeNotice.title}
              description={upgradeNotice.description}
            />
            <div>
              <Typography.Title level={4} style={{ marginBottom: 8 }}>
                Current plan: {currentPlan.plan_name}
              </Typography.Title>
              <div style={{ color: '#64748b', marginBottom: 12 }}>
                {upgradeNotice.requiredFeatureLabel
                  ? `Upgrade if you want access to ${upgradeNotice.requiredFeatureLabel}. Otherwise, you can continue working with the features already included in your current plan.`
                  : 'Upgrade your plan to unlock more features, or continue with the features already included in your current plan.'}
              </div>
              <div>
                {currentPlanFeatures.map((feature) => (
                  <Tag key={feature} color="blue" style={{ marginBottom: 8 }}>
                    {feature}
                  </Tag>
                ))}
              </div>
            </div>
          </Space>
        </Card>
      ) : notice ? (
        <Alert
          type={notice.type}
          showIcon
          title={notice.title}
          description={notice.description}
          closable
          onClose={() => setNotice(null)}
          style={{ marginBottom: 24 }}
        />
      ) : null}

      {sandboxPayment?.enabled ? (
        <Alert
          type="info"
          showIcon
          title="Razorpay test mode is enabled"
          description={sandboxPayment.note || 'Razorpay test mode is enabled. Complete checkout to activate or renew a plan.'}
          style={{ marginBottom: 24 }}
        />
      ) : null}

      {!canUpdateSettings ? (
        <Alert
          type="info"
          showIcon
          title="Read-only billing access"
          description="You can view billing and payment details, but your role cannot change plans or payment methods."
          style={{ marginBottom: 24 }}
        />
      ) : null}

      {!currentPlan ? (
        <Alert
          type="warning"
          showIcon
          title="No active seller plan yet"
          description="Choose a plan below and complete Razorpay checkout to unlock seller dashboard access."
          style={{ marginBottom: 24 }}
        />
      ) : null}

      {accessState?.in_grace_period ? (
        <Alert
          type="warning"
          showIcon
          title="Your plan is in the 24-hour renewal buffer"
          description={`You still have dashboard access. Renew within ${accessState.buffer_hours_remaining} hour(s) to avoid access being paused.`}
          style={{ marginBottom: 24 }}
        />
      ) : null}

      {currentPlan && !accessState?.has_operational_access ? (
        <Alert
          type="error"
          showIcon
          title="Seller access is paused"
          description="The 24-hour renewal buffer has ended. Activate or renew a plan below to restore dashboard access."
          style={{ marginBottom: 24 }}
        />
      ) : null}

      {currentPlan ? (
        <Card
          title="Features available now"
          style={{ marginBottom: 24, borderColor: '#16a34a', background: 'linear-gradient(180deg, rgba(34, 197, 94, 0.10) 0%, #ffffff 42%)' }}
          extra={<Tag color="green">Unlocked with current plan</Tag>}
        >
          <Space direction="vertical" size="middle" style={{ width: '100%' }}>
            <Typography.Text type="secondary">
              These features are already available to use on your active plan. AI features stay in the coming soon section until they launch.
            </Typography.Text>
            <Space wrap>
              {currentPlanFeatures.length > 0 ? currentPlanFeatures.map((feature, index) => (
                <Tag key={`${feature}-${index}`} color={index % 3 === 0 ? 'green' : index % 3 === 1 ? 'blue' : 'cyan'}>
                  {feature}
                </Tag>
              )) : <Tag color="default">No features listed</Tag>}
            </Space>
            {currentPlanFeatureKeys.length > 0 ? (
              <Typography.Text type="secondary">
                Feature keys: {currentPlanFeatureKeys.join(', ')}
              </Typography.Text>
            ) : null}
          </Space>
        </Card>
      ) : null}

      <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
        <Col xs={24} xl={24}>
          <Card
            title={upgradeNotice ? 'Upgrade options' : 'Choose a seller plan'}
            extra={<Segmented<'monthly' | 'yearly'> options={[{ label: 'Monthly', value: 'monthly' }, { label: 'Yearly', value: 'yearly' }]} value={selectedBillingCycle} onChange={setSelectedBillingCycle} />}
            loading={loading}
          >
            <div style={{ display: 'grid', gap: 16 }}>
              <div style={{ color: '#64748b' }}>
                Compare plans and choose the right tier for your seller operations. Billing updates apply immediately after Razorpay confirmation.
              </div>
              <Row gutter={[16, 16]}>
                {displayPlans.map((plan) => {
                  const price = selectedBillingCycle === 'yearly' ? plan.yearly_price : plan.monthly_price
                  const isCurrent = currentPlan?.plan_code === plan.code && currentPlan?.billing_cycle === selectedBillingCycle
                  const isPopular = plan.code === popularPlanCode
                  const badgeLabel = isCurrent ? 'Current' : isPopular ? 'Most popular' : null
                  const cardAccent = isPopular ? '#0f766e' : '#e2e8f0'
                  return (
                    <Col xs={24} md={12} xl={8} key={plan.code}>
                      <Card
                        size="small"
                        style={{
                          borderColor: isCurrent ? '#1677ff' : cardAccent,
                          borderTopWidth: isPopular ? 3 : undefined,
                          boxShadow: isPopular
                            ? '0 20px 40px rgba(15, 118, 110, 0.18)'
                            : '0 12px 28px rgba(15, 23, 42, 0.08)',
                          background: isPopular
                            ? 'linear-gradient(180deg, rgba(15, 118, 110, 0.08) 0%, #ffffff 38%)'
                            : '#ffffff',
                          minHeight: 360,
                        }}
                      >
                        <Space orientation="vertical" size="middle" style={{ width: '100%' }}>
                          <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12 }}>
                            <div>
                              <Typography.Title level={4} style={{ margin: 0 }}>{plan.name}</Typography.Title>
                              <div style={{ color: '#64748b', minHeight: 44 }}>{plan.description}</div>
                            </div>
                            {badgeLabel ? <Tag color={isCurrent ? 'blue' : 'green'}>{badgeLabel}</Tag> : null}
                          </div>
                          <div style={{ fontSize: 30, fontWeight: 700, color: '#0f172a' }}>
                            {formatCurrency(price, plan.currency_symbol)}
                            <span style={{ fontSize: 13, color: '#64748b', marginLeft: 8 }}>
                              / {selectedBillingCycle === 'yearly' ? 'year' : 'month'}
                            </span>
                          </div>
                          <div style={{ color: '#64748b', fontSize: 12 }}>
                            Billed {selectedBillingCycle === 'yearly' ? 'yearly' : 'monthly'}.
                          </div>
                          <ul style={{ listStyle: 'none', margin: 0, padding: 0, display: 'grid', gap: 8 }}>
                            {plan.features.map((feature) => (
                              <li key={feature} style={{ display: 'flex', gap: 8, alignItems: 'flex-start', color: '#0f172a' }}>
                                <CheckCircleOutlined style={{ color: '#0f766e', marginTop: 2 }} />
                                <span>{feature}</span>
                              </li>
                            ))}
                          </ul>
                          <Button type={isCurrent ? 'default' : 'primary'} block disabled={saving || !canUpdateSettings} onClick={() => void renewPlan(plan.code)}>
                            {isCurrent ? 'Current Plan' : currentPlan ? `Switch to ${plan.name}` : `Activate ${plan.name}`}
                          </Button>
                        </Space>
                      </Card>
                    </Col>
                  )
                })}
              </Row>
            </div>
          </Card>
        </Col>
      </Row>

      <Card title="Plan add-ons" style={{ marginBottom: 24 }} loading={loading}>
        <Space orientation="vertical" size="large" style={{ width: '100%' }}>
          {effectiveEntitlements ? (
            <Row gutter={[12, 12]}>
              <Col xs={24} md={12}>
                <Card size="small">
                  <Typography.Text type="secondary">Effective product limit</Typography.Text>
                  <div style={{ fontSize: 24, fontWeight: 800 }}>
                    {effectiveEntitlements.product_limit ?? 'N/A'}
                    {Number(effectiveEntitlements.product_limit_increment ?? 0) > 0 ? (
                      <Tag color="green" style={{ marginLeft: 8 }}>
                        +{effectiveEntitlements.product_limit_increment}
                      </Tag>
                    ) : null}
                  </div>
                </Card>
              </Col>
              <Col xs={24} md={12}>
                <Card size="small">
                  <Typography.Text type="secondary">Effective staff limit</Typography.Text>
                  <div style={{ fontSize: 24, fontWeight: 800 }}>
                    {effectiveEntitlements.staff_limit ?? 'N/A'}
                    {Number(effectiveEntitlements.staff_limit_increment ?? 0) > 0 ? (
                      <Tag color="green" style={{ marginLeft: 8 }}>
                        +{effectiveEntitlements.staff_limit_increment}
                      </Tag>
                    ) : null}
                  </div>
                </Card>
              </Col>
            </Row>
          ) : null}

          {productPolicy?.variant_policy ? (
            <Alert
              type="info"
              showIcon
              title="Product counting policy"
              description={`${productPolicy.variant_policy}${productPolicy.example ? ` Example: ${productPolicy.example}.` : ''}`}
            />
          ) : null}

          <Alert
            type="info"
            showIcon
            title="Monthly add-on billing"
            description="Add-ons are purchased separately each month. Pay through Razorpay when you buy or renew an add-on. They are not bundled into your plan subscription invoice."
          />

          {!currentPlan || currentPlan.status !== 'active' ? (
            <Alert
              type="warning"
              showIcon
              title="Active plan required"
              description="Activate your seller plan before purchasing add-ons."
            />
          ) : null}

          {activeAddOns.length > 0 ? (
            <div>
              <Typography.Title level={5}>Active add-ons</Typography.Title>
              <Space wrap>
                {activeAddOns.map((addOn) => (
                  <Tag key={addOn.id} color="blue">
                    {addOn.add_on_name} - {formatCurrency(Number(addOn.amount ?? 0), currencySymbol)} / month
                    {addOn.ends_at ? ` (until ${new Date(addOn.ends_at).toLocaleDateString()})` : ''}
                  </Tag>
                ))}
              </Space>
            </div>
          ) : null}

          {addOnGroups.map((group) => (
            <div key={group.group || group.name}>
              <Typography.Title level={5} style={{ marginBottom: 4 }}>{group.name}</Typography.Title>
              {group.description ? <Typography.Text type="secondary">{group.description}</Typography.Text> : null}
              <Row gutter={[12, 12]} style={{ marginTop: 12 }}>
                {group.items.map((addOn) => {
                  const isActive = activeAddOnCodes.has(addOn.code)
                  return (
                    <Col xs={24} md={12} xl={8} key={addOn.code}>
                      <Card
                        size="small"
                        style={{
                          height: '100%',
                          borderColor: isActive ? '#1677ff' : '#e2e8f0',
                        }}
                      >
                        <Space orientation="vertical" size="small" style={{ width: '100%' }}>
                          <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8 }}>
                            <Typography.Text strong>{addOn.name}</Typography.Text>
                            {isActive ? <Tag color="blue">Active</Tag> : null}
                          </div>
                          <div style={{ fontSize: 22, fontWeight: 800 }}>
                            {formatCurrency(Number(addOn.monthly_price ?? 0), addOn.currency_symbol)}
                            <span style={{ fontSize: 12, color: '#64748b', marginLeft: 6 }}>/ month</span>
                          </div>
                          <Space wrap>
                            {Number(addOn.product_limit_increment ?? 0) > 0 ? <Tag>+{addOn.product_limit_increment} products</Tag> : null}
                            {Number(addOn.staff_limit_increment ?? 0) > 0 ? <Tag>+{addOn.staff_limit_increment} staff</Tag> : null}
                            {(addOn.features ?? []).map((feature) => (
                              <Tag key={`${addOn.code}-${feature}`} color="green">{feature}</Tag>
                            ))}
                          </Space>
                          <Button
                            type={isActive ? 'default' : 'primary'}
                            block
                            disabled={
                              saving
                              || !canUpdateSettings
                              || isActive
                              || !currentPlan
                              || currentPlan?.status !== 'active'
                            }
                            onClick={() => void purchaseAddOn(addOn.code)}
                          >
                            {isActive ? 'Active this month' : 'Purchase add-on'}
                          </Button>
                        </Space>
                      </Card>
                    </Col>
                  )
                })}
              </Row>
            </div>
          ))}
        </Space>
      </Card>

      <Card
        title="AI feature roadmap"
        extra={<Tag color="gold">{aiFeatureCount} coming soon</Tag>}
        style={{ marginBottom: 24 }}
        loading={loading}
      >
        {featureRoadmap.length > 0 ? (
          <Row gutter={[16, 16]}>
            {featureRoadmap.map((feature) => (
              <Col xs={24} md={12} xl={8} key={feature.code}>
                <Card size="small" style={{ height: '100%', borderColor: '#fde68a', background: 'linear-gradient(180deg, rgba(250, 204, 21, 0.10) 0%, #ffffff 40%)' }}>
                  <Space direction="vertical" size={8} style={{ width: '100%' }}>
                    <Tag color="gold">Coming soon</Tag>
                    <Typography.Title level={5} style={{ margin: 0 }}>{feature.name}</Typography.Title>
                    <Typography.Text type="secondary">{feature.description || 'This AI feature is planned for a future release.'}</Typography.Text>
                  </Space>
                </Card>
              </Col>
            ))}
          </Row>
        ) : (
          <Alert
            type="info"
            showIcon
            title="No AI features are active yet"
            description="AI-based seller helpers are planned for a future release and will appear here as coming soon when they are added to your plan catalog."
          />
        )}
      </Card>

      {comparisonRows.length > 0 ? (
        <Card title="Plan comparison" style={{ marginBottom: 24 }}>
          {Object.entries(comparisonGroups).map(([group, rows]) => (
            <div key={group} style={{ marginBottom: 20 }}>
              <div style={{ fontWeight: 700, color: '#0f172a', marginBottom: 10 }}>{group}</div>
              <div style={{ border: '1px solid #e2e8f0', borderRadius: 12, overflowX: 'auto' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 720 }}>
                  <thead>
                    <tr style={{ background: '#f8fafc' }}>
                      <th style={{ textAlign: 'left', padding: '12px 14px', borderRight: '1px solid #e2e8f0', width: 220 }}>Feature</th>
                      {displayPlans.map((plan) => {
                        const price = selectedBillingCycle === 'yearly' ? plan.yearly_price : plan.monthly_price
                        const isPopular = plan.code === popularPlanCode
                        return (
                          <th
                            key={`${plan.code}-${plan.name}`}
                            style={{
                              textAlign: 'left',
                              padding: '12px 14px',
                              borderRight: '1px solid #e2e8f0',
                              background: isPopular ? 'rgba(15, 118, 110, 0.08)' : undefined,
                            }}
                          >
                            <div style={{ fontWeight: 700 }}>{plan.name}</div>
                            <div style={{ fontSize: 12, color: '#64748b' }}>
                              {formatCurrency(price, plan.currency_symbol)} / {selectedBillingCycle === 'yearly' ? 'year' : 'month'}
                            </div>
                          </th>
                        )
                      })}
                    </tr>
                  </thead>
                  <tbody>
                    {rows.map((row) => (
                      <tr key={row.key}>
                        <td style={{ padding: '12px 14px', borderTop: '1px solid #e2e8f0', borderRight: '1px solid #e2e8f0', fontWeight: 600, color: '#0f172a' }}>
                          {row.label}
                        </td>
                        {displayPlans.map((plan) => {
                          const value = plan.comparison?.[row.key] || 'N/A'
                          const normalizedValue = String(value)
                          const isMuted = normalizedValue.toLowerCase().includes('not included') || normalizedValue === 'N/A'
                          const isPopular = plan.code === popularPlanCode

                          return (
                            <td
                              key={`${plan.code}-${plan.name}`}
                              style={{
                                padding: '12px 14px',
                                borderTop: '1px solid #e2e8f0',
                                borderRight: '1px solid #e2e8f0',
                                color: isMuted ? '#94a3b8' : '#334155',
                                fontWeight: isMuted ? 500 : 600,
                                background: isPopular ? 'rgba(15, 118, 110, 0.04)' : undefined,
                              }}
                            >
                              {normalizedValue}
                            </td>
                          )
                        })}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          ))}
        </Card>
      ) : null}

      <Tabs
        items={[
          {
            key: 'billings',
            label: 'Billing History',
            children: (
              <Card extra={<Tag color="blue">24-hour renewal buffer after expiry</Tag>}>
                <Table columns={billingColumns} dataSource={sortedBillings} rowKey="id" loading={loading} pagination={{ pageSize: 8 }} scroll={{ x: 980 }} />
              </Card>
            ),
          },
          {
            key: 'payments',
            label: 'Payment Preferences',
            children: (
              <Card extra={canUpdateSettings ? <Button type="primary" icon={<CreditCardOutlined />} onClick={() => setPaymentModalVisible(true)}>Add Preference</Button> : <Tag color="default">Read only</Tag>}>
                <Table columns={paymentColumns} dataSource={paymentMethods} rowKey="id" loading={loading} pagination={false} />
              </Card>
            ),
          },
        ]}
      />

      <Modal title="Add Payment Preference" open={paymentModalVisible} onCancel={() => setPaymentModalVisible(false)} footer={null}>
        <Form form={paymentForm} layout="vertical" onFinish={addPaymentMethod} initialValues={{ type: 'card' }}>
          {sandboxPayment?.enabled ? (
            <Alert
              type="info"
              showIcon
              title="Razorpay test mode"
              description={sandboxPayment.note || 'Razorpay test mode is enabled. Use any test payment method to continue.'}
              style={{ marginBottom: 16 }}
            />
          ) : null}
          <Form.Item name="type" label="Payment Type" rules={[{ required: true, message: 'Payment type is required' }]}> 
            <Segmented
              options={[
                { label: 'Card', value: 'card' },
                { label: 'UPI', value: 'upi' },
                { label: 'Wallet', value: 'wallet' },
                { label: 'Netbanking', value: 'netbanking' },
              ]}
            />
          </Form.Item>
          <Form.Item name="label" label="Label (optional)"> 
            <Input placeholder="e.g. Primary UPI" />
          </Form.Item>

          {paymentType === 'card' ? (
            <>
              <Form.Item name="cardholder_name" label="Cardholder Name" rules={[{ required: true, message: 'Cardholder name is required' }]}> 
                <Input placeholder="Name on card" />
              </Form.Item>
              <Form.Item name="card_number" label="Card Number" rules={[{ required: true, message: 'Card number is required' }, { min: 12, message: 'Card number must be at least 12 digits' }]}> 
                <Input maxLength={23} placeholder="1234123412341234" />
              </Form.Item>
              <Row gutter={12}>
                <Col span={12}>
                  <Form.Item name="expiry_month" label="Expiry Month" rules={[{ required: true, message: 'Expiry month is required' }]}> 
                    <InputNumber min={1} max={12} style={{ width: '100%' }} />
                  </Form.Item>
                </Col>
                <Col span={12}>
                  <Form.Item name="expiry_year" label="Expiry Year" rules={[{ required: true, message: 'Expiry year is required' }]}> 
                    <InputNumber min={new Date().getFullYear()} max={new Date().getFullYear() + 15} style={{ width: '100%' }} />
                  </Form.Item>
                </Col>
              </Row>
            </>
          ) : null}

          {paymentType === 'upi' ? (
            <Form.Item name="upi_id" label="UPI ID" rules={[{ required: true, message: 'UPI ID is required' }]}> 
              <Input placeholder="name@bank" />
            </Form.Item>
          ) : null}

          {paymentType === 'wallet' ? (
            <Form.Item name="wallet_name" label="Wallet Name" rules={[{ required: true, message: 'Wallet name is required' }]}> 
              <Input placeholder="Paytm / PhonePe / Amazon Pay" />
            </Form.Item>
          ) : null}

          {paymentType === 'netbanking' ? (
            <Form.Item name="bank_name" label="Bank Name" rules={[{ required: true, message: 'Bank name is required' }]}> 
              <Input placeholder="HDFC / ICICI / SBI" />
            </Form.Item>
          ) : null}

          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 12 }}>
            <Button onClick={() => setPaymentModalVisible(false)}>Cancel</Button>
            <Button type="primary" htmlType="submit" loading={saving}>
              Save Preference
            </Button>
          </div>
        </Form>
      </Modal>
    </div>
  )
}

export default SettingsPage


